import { Suspense, type ReactNode } from 'react';
import { useLingui } from '@lingui/react/macro';
import { useSuspenseQuery } from '@tanstack/react-query';
import { formatNumber } from '@worldcoo/ui/lib/utils';
import { trpc } from '@/lib/trpc';
import { useTranslation } from '@/lib/i18n';
import { useLiveDonationTotals } from '@/routes/-components/use-live-donation-totals';
import { RollingFigure } from '../rolling-figure';
import { Expand, ExpandGroup } from '../expand';
import { SwipeRow, DRIFT_PX_PER_MS } from './swipe-row';
import {
	CoinsTile,
	FundedProjectsTile,
	MicrodonationsTile,
	PartnerNgosTile,
	PhotoTile,
	TotalRaisedTile,
} from './stat-tiles';

const MOBILE_STAT = 'h-44 w-[17rem] shrink-0 snap-start';
const MOBILE_PHOTO = 'h-44 w-52 shrink-0 snap-start';

// Spanish does not group four digits (1303), and these must read as thousands: docs/notes/portals/www-live-figures.md
const GROUPED = { alwaysGroup: true };

/**
 * Cents on the money reverse TEC-3832's "rounded to the euro": a total that moves only per whole euro
 * sits still for most of a minute. The fallback uses the same digits, or the tile reformats on landing.
 */
const MONEY_DIGITS = 2;
const COUNT_DIGITS = 0;

export function Stats() {
	const { language } = useTranslation();
	const { data: stats } = useSuspenseQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());

	// Not rounded, so the fallback renders the same string the live counter will.
	const totalRaised = `${formatNumber(language, stats.totalRaised.amount, MONEY_DIGITS, GROUPED)} €`;
	const microDonations = formatNumber(language, stats.totalDonations, COUNT_DIGITS, GROUPED);

	return (
		// First load only: the live query's key is stable, so a window rollover refetches without suspending.
		<Suspense fallback={<StatsBody raised={totalRaised} microdonations={microDonations} />}>
			<LiveFigures />
		</Suspense>
	);
}

/** One `useLiveDonationTotals` pass feeds both, so a donation always adds its amount and exactly one unit. */
function LiveFigures() {
	const { language } = useTranslation();
	const { raisedEuros, donationCount } = useLiveDonationTotals();

	return (
		<StatsBody
			raised={<RollingFigure locale={language} value={raisedEuros} suffix="€" decimalDigits={MONEY_DIGITS} />}
			microdonations={<RollingFigure locale={language} value={donationCount} decimalDigits={COUNT_DIGITS} />}
		/>
	);
}

/** Mobile is two swipe rows: a single stacked column pushes the page down by a screen and a half. */
function StatsBody(props: { raised: ReactNode; microdonations: ReactNode }) {
	const { t } = useLingui();
	const { language } = useTranslation();
	const { data: stats } = useSuspenseQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());

	const projectsFunded = formatNumber(language, stats.projectsFunded, 0, GROUPED);
	const partnerNgos = formatNumber(language, stats.socialOrganizations, 0, GROUPED);
	const totalRaised = props.raised;
	const microDonations = props.microdonations;

	const kids = { src: '/redesign/hero-kids.jpg', alt: t`Children we help` };
	const girl = { src: '/redesign/hero-girl.jpg', alt: t`A child smiling` };

	return (
		// One trigger for the bento, so the arrival order is the design's, not the reader's scroll speed.
		<ExpandGroup asChild>
			<div className="relative pb-16 md:pb-24">
				{/* Anchored to the bento and clipped by the section (the design's half-crop); under 1280px it is a sliver. */}
				<Expand index={0} asChild>
					<PhotoTile
						{...kids}
						className="absolute top-30 right-[calc(50%_+_624px)] hidden h-96 w-[13.75rem] xl:block"
					/>
				</Expand>
				<Expand index={4} asChild>
					<PhotoTile {...girl} className="absolute top-30 left-[calc(50%_+_624px)] hidden h-96 w-[13.75rem] xl:block" />
				</Expand>

				{/* Each row leads with a headline figure; different speeds and half-beat offsets keep them reading as two rows. */}
				<div className="flex flex-col gap-4 md:hidden">
					<SwipeRow speed={DRIFT_PX_PER_MS}>
						<Expand index={0} asChild>
							<TotalRaisedTile value={totalRaised} className={MOBILE_STAT} />
						</Expand>
						<Expand index={1} asChild>
							<FundedProjectsTile value={projectsFunded} className={MOBILE_STAT} />
						</Expand>
						<Expand index={2} asChild>
							<PhotoTile {...girl} className={MOBILE_PHOTO} />
						</Expand>
					</SwipeRow>
					<SwipeRow speed={DRIFT_PX_PER_MS * 1.35}>
						<Expand index={0.5} asChild>
							<MicrodonationsTile value={microDonations} className={MOBILE_STAT} />
						</Expand>
						<Expand index={1.5} asChild>
							<PartnerNgosTile value={partnerNgos} className={MOBILE_STAT} />
						</Expand>
						<Expand index={2.5} asChild>
							<CoinsTile className={MOBILE_STAT} />
						</Expand>
					</SwipeRow>
				</div>

				{/* The hand-off staggers the columns; `items-start` keeps the grid from stretching them to one height. */}
				<div className="mx-auto hidden max-w-[1200px] grid-cols-3 items-start gap-4 px-6 md:grid xl:px-0">
					<div className="mt-10 flex flex-col gap-4">
						<Expand index={1} asChild>
							<FundedProjectsTile value={projectsFunded} className="h-64" />
						</Expand>
						<Expand index={1.5} asChild>
							<CoinsTile className="h-76" />
						</Expand>
					</div>
					<Expand index={2} asChild>
						<TotalRaisedTile value={totalRaised} className="mt-20 h-136" />
					</Expand>
					<div className="flex flex-col gap-4">
						<Expand index={3} asChild>
							<MicrodonationsTile value={microDonations} className="h-96" />
						</Expand>
						<Expand index={3.5} asChild>
							<PartnerNgosTile value={partnerNgos} className="h-72" />
						</Expand>
					</div>
				</div>
			</div>
		</ExpandGroup>
	);
}
